import { Component, OnInit } from '@angular/core';
import { ApiService } from '../../_core/_services/api.service';
import {
  PaymentWebhookLogDetailsDto,
  PaymentWebhookLogListItemDto
} from '../../_core/_model/payment-webhook-log';
import { UserDto } from '../../_core/_model/users';

@Component({
  selector: 'app-payment-webhook-logs',
  standalone: false,
  templateUrl: './payment-webhook-logs.html',
  styleUrl: './payment-webhook-logs.css'
})
export class PaymentWebhookLogs implements OnInit {
  loading = false;
  detailLoading = false;
  resolving = false;
  usersLoading = false;

  items: PaymentWebhookLogListItemDto[] = [];
  selected: PaymentWebhookLogDetailsDto | null = null;

  providerCode = '';
  processingStatus = '';
  search = '';

  page = 1;
  rowsPerPage = 20;
  totalPages = 1;
  totalItems = 0;

  showDetailsModal = false;
  showResolveModal = false;

  selectedResolveLog: PaymentWebhookLogListItemDto | null = null;
  users: UserDto[] = [];
  resolveUserId = '';
  resolveNote = '';
  resolveUserSearch = '';

  constructor(private api: ApiService) {}

  ngOnInit(): void {
    this.loadLogs();
  }

  loadLogs(): void {
    this.loading = true;

    this.api.getPaymentWebhookLogs({
      providerCode: this.providerCode || undefined,
      processingStatus: this.processingStatus || undefined,
      search: this.search || undefined,
      page: this.page,
      rowsPerPage: this.rowsPerPage
    }).subscribe({
      next: (res) => {
        this.items = res.data || [];
        this.totalPages = res.meta?.totalPages || 1;
        this.totalItems = res.meta?.totalRows || this.items.length;
      },
      complete: () => {
        this.loading = false;
      }
    });
  }

  resetFilters(): void {
    this.providerCode = '';
    this.processingStatus = '';
    this.search = '';
    this.page = 1;
    this.loadLogs();
  }

  openDetails(item: PaymentWebhookLogListItemDto): void {
    this.detailLoading = true;
    this.selected = null;
    this.showDetailsModal = true;

    this.api.getPaymentWebhookLog(item.id).subscribe({
      next: (res) => {
        this.selected = res.data;
      },
      complete: () => {
        this.detailLoading = false;
      }
    });
  }

  closeDetails(): void {
    this.showDetailsModal = false;
    this.selected = null;
  }

  canResolveBlaaizCadWebhook(item: PaymentWebhookLogListItemDto): boolean {
    return item.providerCode === 'BLAAIZ'
      && item.processingStatus === 'UnmatchedSenderEmail';
  }

  openResolveModal(item: PaymentWebhookLogListItemDto): void {
    this.selectedResolveLog = item;
    this.resolveUserId = '';
    this.resolveNote = '';
    this.resolveUserSearch = '';
    this.showResolveModal = true;
    this.loadUsersForResolution();
  }

  closeResolveModal(): void {
    if (this.resolving) return;

    this.showResolveModal = false;
    this.selectedResolveLog = null;
    this.resolveUserId = '';
    this.resolveNote = '';
    this.resolveUserSearch = '';
  }

  loadUsersForResolution(): void {
    this.usersLoading = true;

    this.api.getUsers(1).subscribe({
      next: (res) => {
        this.users = res.data || [];
      },
      complete: () => {
        this.usersLoading = false;
      }
    });
  }

  get filteredUsers(): UserDto[] {
    const q = (this.resolveUserSearch || '').trim().toLowerCase();

    if (!q) return this.users;

    return this.users.filter(u =>
      `${u.firstName} ${u.lastName}`.toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q) ||
      (u.id || '').toLowerCase().includes(q)
    );
  }

  resolveWebhook(): void {
    if (!this.selectedResolveLog) return;

    if (!this.resolveUserId) {
      alert('Please select a user to resolve this webhook.');
      return;
    }

    this.resolving = true;

    this.api.resolveBlaaizCadDepositWebhook(
      this.selectedResolveLog.id,
      this.resolveUserId,
      this.resolveNote
    ).subscribe({
      next: () => {
        this.resolving = false;
        this.closeResolveModal();
        this.loadLogs();
      },
      error: (err) => {
        this.resolving = false;
        alert(
          err.error?.Error?.Details ||
          err.error?.message ||
          'Failed to resolve webhook.'
        );
      }
    });
  }

  nextPage(): void {
    if (this.page >= this.totalPages) return;
    this.page++;
    this.loadLogs();
  }

  previousPage(): void {
    if (this.page <= 1) return;
    this.page--;
    this.loadLogs();
  }

  getStatusClass(status: string): string {
    const value = (status || '').toLowerCase();

    if (value === 'processed') return 'badge text-bg-success';
    if (value === 'failed') return 'badge text-bg-danger';
    if (value === 'received') return 'badge text-bg-warning';
    if (value === 'unmatchedsenderemail') return 'badge text-bg-info';
    if (value === 'resolving') return 'badge text-bg-primary';

    return 'badge text-bg-secondary';
  }

  formatJson(value?: string | null): string {
    if (!value) return '';

    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      return value;
    }
  }
}